import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  message,
  Row,
  Col,
  Typography,
  Divider,
  Select,
  Descriptions,
  Space,
  Spin,
} from 'antd';
import {
  SaveOutlined,
  UploadOutlined,
  GlobalOutlined,
  MailOutlined,
  SettingOutlined,
  EditOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useTenantStore } from '../../stores/tenantStore';
import { useAuthStore } from '../../stores/authStore';
import { tenantApi } from '../../services/tenant';
import type { Tenant, UpdateTenantRequest } from '../../types/tenant';
import { COLORS } from '../../constants/colors';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// 行业选项
const industryOptions = [
  { value: 'technology', label: '科技/互联网' },
  { value: 'ecommerce', label: '电商/零售' },
  { value: 'finance', label: '金融/保险' },
  { value: 'education', label: '教育/培训' },
  { value: 'healthcare', label: '医疗/健康' },
  { value: 'entertainment', label: '娱乐/传媒' },
  { value: 'manufacturing', label: '制造业' },
  { value: 'consulting', label: '咨询/服务' },
  { value: 'other', label: '其他' },
];

// 公司规模选项
const sizeOptions = [
  { value: 'small', label: '小型 (1-50人)' },
  { value: 'medium', label: '中型 (51-200人)' },
  { value: 'large', label: '大型 (200人以上)' },
];

export const TenantSettings: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  
  const { tenant, isLoading, fetchTenant } = useTenantStore();
  const { user } = useAuthStore();

  // 是否有编辑权限（只有 Owner 和 Admin 可以编辑）
  const canEdit = user?.role === 'owner' || user?.role === 'admin';

  useEffect(() => {
    if (!tenant) {
      fetchTenant();
    }
  }, [tenant, fetchTenant]);

  // 当租户数据加载完成后，设置表单初始值
  useEffect(() => {
    if (tenant && isEditing) {
      form.setFieldsValue({
        company_name: tenant.company_name,
        company_industry: tenant.company_industry,
        company_size: tenant.company_size,
        contact_email: tenant.contact_email,
        contact_phone: tenant.contact_phone,
      });
    }
  }, [tenant, isEditing, form]);

  const handleEdit = () => {
    setIsEditing(true);
    if (tenant) {
      form.setFieldsValue({
        company_name: tenant.company_name,
        company_industry: tenant.company_industry,
        company_size: tenant.company_size,
        contact_email: tenant.contact_email,
        contact_phone: tenant.contact_phone,
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    form.resetFields();
  };

  const handleSave = async (values: UpdateTenantRequest) => {
    setSaving(true);
    try {
      await tenantApi.updateTenant(values);
      await fetchTenant();
      setIsEditing(false);
      message.success('设置保存成功');
    } catch {
      message.error('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const getIndustryLabel = (value?: string) => {
    return industryOptions.find(opt => opt.value === value)?.label || value || '-';
  };

  const getSizeLabel = (value?: string) => {
    return sizeOptions.find(opt => opt.value === value)?.label || value || '-';
  };

  const getStatusTag = (status?: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      trial: { color: 'default', text: '试用中' },
      active: { color: 'success', text: '正常' },
      frozen: { color: 'error', text: '已冻结' },
      expired: { color: 'warning', text: '已过期' },
    };
    const config = statusMap[status || ''] || { color: 'default', text: status || '-' };
    return <span style={{ color: config.color === 'success' ? COLORS.success : config.color === 'error' ? COLORS.error : config.color === 'warning' ? COLORS.warning : undefined }}>{config.text}</span>;
  };

  const getPlanLabel = (plan?: string) => {
    const planMap: Record<string, string> = {
      trial: '试用版',
      starter: '入门版',
      professional: '专业版',
      enterprise: '企业版',
    };
    return planMap[plan || ''] || plan || '-';
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          租户设置
        </Title>
        {canEdit && !isEditing && (
          <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
            编辑信息
          </Button>
        )}
      </div>

      <Row gutter={24}>
        <Col span={16}>
          <Card>
            {!isEditing ? (
              // 展示模式
              <>
                {/* 基本信息 */}
                <div style={{ marginBottom: 24 }}>
                  <Title level={5}>
                    <GlobalOutlined style={{ marginRight: 8 }} />
                    基本信息
                  </Title>
                  <Divider style={{ margin: '12px 0' }} />
                  <Descriptions column={2} bordered size="small">
                    <Descriptions.Item label="公司名称">{tenant?.company_name || '-'}</Descriptions.Item>
                    <Descriptions.Item label="所属行业">{getIndustryLabel(tenant?.company_industry)}</Descriptions.Item>
                    <Descriptions.Item label="公司规模">{getSizeLabel(tenant?.company_size)}</Descriptions.Item>
                    <Descriptions.Item label="Logo">
                      {tenant?.company_logo_url ? (
                        <img src={tenant.company_logo_url} alt="logo" style={{ maxWidth: 60, maxHeight: 60 }} />
                      ) : (
                        <Text type="secondary">未上传</Text>
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </div>

                {/* 联系信息 */}
                <div style={{ marginBottom: 24 }}>
                  <Title level={5}>
                    <MailOutlined style={{ marginRight: 8 }} />
                    联系信息
                  </Title>
                  <Divider style={{ margin: '12px 0' }} />
                  <Descriptions column={2} bordered size="small">
                    <Descriptions.Item label="联系邮箱">{tenant?.contact_email || '-'}</Descriptions.Item>
                    <Descriptions.Item label="联系电话">{tenant?.contact_phone || '-'}</Descriptions.Item>
                  </Descriptions>
                </div>

                {/* 订阅信息 */}
                <div>
                  <Title level={5}>
                    <SettingOutlined style={{ marginRight: 8 }} />
                    订阅信息
                  </Title>
                  <Divider style={{ margin: '12px 0' }} />
                  <Descriptions column={2} bordered size="small">
                    <Descriptions.Item label="当前套餐">{getPlanLabel(tenant?.subscription_plan)}</Descriptions.Item>
                    <Descriptions.Item label="状态">{getStatusTag(tenant?.status)}</Descriptions.Item>
                    <Descriptions.Item label="成员数">{(tenant as Tenant & { member_count?: number; member_limit?: number }).member_count || 0} / {(tenant as Tenant & { member_count?: number; member_limit?: number }).member_limit || 0}</Descriptions.Item>
                    <Descriptions.Item label="创建时间">{tenant?.created_at || '-'}</Descriptions.Item>
                    {tenant?.trial_started_at && (
                      <Descriptions.Item label="试用开始时间">{tenant.trial_started_at}</Descriptions.Item>
                    )}
                    {tenant?.trial_ended_at && (
                      <Descriptions.Item label="试用结束时间">{tenant.trial_ended_at}</Descriptions.Item>
                    )}
                  </Descriptions>
                </div>
              </>
            ) : (
              // 编辑模式
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
              >
                {/* 基本信息 */}
                <div style={{ marginBottom: 24 }}>
                  <Title level={5}>
                    <GlobalOutlined style={{ marginRight: 8 }} />
                    基本信息
                  </Title>
                  <Divider style={{ margin: '12px 0' }} />

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="company_name"
                        label="公司名称"
                        rules={[{ required: true, message: '请输入公司名称' }]}
                      >
                        <Input placeholder="输入公司名称" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="company_industry" label="所属行业">
                        <Select placeholder="选择行业" allowClear>
                          {industryOptions.map(opt => (
                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="company_size" label="公司规模">
                        <Select placeholder="选择公司规模" allowClear>
                          {sizeOptions.map(opt => (
                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="company_logo_url" label="Logo URL">
                        <Input placeholder="输入Logo URL" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item name="logo_upload" label="上传Logo">
                    <Upload
                      name="logo"
                      listType="picture-card"
                      showUploadList={false}
                      beforeUpload={() => {
                        message.info('Logo上传功能暂未开放，请直接填写Logo URL');
                        return false;
                      }}
                    >
                      <div>
                        <UploadOutlined />
                        <div style={{ marginTop: 8 }}>上传Logo</div>
                      </div>
                    </Upload>
                  </Form.Item>
                </div>

                {/* 联系信息 */}
                <div style={{ marginBottom: 24 }}>
                  <Title level={5}>
                    <MailOutlined style={{ marginRight: 8 }} />
                    联系信息
                  </Title>
                  <Divider style={{ margin: '12px 0' }} />

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="contact_email" label="联系邮箱">
                        <Input placeholder="contact@company.com" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="contact_phone" label="联系电话">
                        <Input placeholder="输入联系电话" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                      保存设置
                    </Button>
                    <Button icon={<CloseOutlined />} onClick={handleCancel}>
                      取消
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            )}
          </Card>
        </Col>

        <Col span={8}>
          <Card title="帮助信息">
            <Text type="secondary">
              <p><strong>公司名称：</strong>显示在系统各处的组织名称</p>
              <p><strong>所属行业：</strong>用于系统优化推荐</p>
              <p><strong>公司规模：</strong>影响系统配额设置</p>
              <p><strong>联系信息：</strong>用于系统通知和客服联系</p>
            </Text>
          </Card>
          
          {!canEdit && (
            <Card style={{ marginTop: 16 }}>
              <Text type="secondary">
                只有所有者和管理员可以编辑租户信息
              </Text>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};
